import { MarketButton, MarketLink } from '@market/react';
import { useTranslation, Trans } from 'react-i18next';
import { withI18n } from '@squareup/service-i18n/components';
import { InstantAccessProgram_Platform } from '@squareup/shared-types-protos/squareup/inflows/instant_access_program_model';
import type { DDSEligibleUnitInfo_IntegrationToOptInStatus as IntegrationToOptInStatus } from '@squareup/shared-types-protos/squareup/inflows/service/external_instant_access_service.js';
import setupPageHeroLargePng from '../../assets/images/setup-page-hero-large.png';
import setupPageHeroLargeWebp from '../../assets/images/setup-page-hero-large.webp';
import setupPageHeroLarge2xPng from '../../assets/images/setup-page-hero-large@2x.png';
import setupPageHeroLarge3xPng from '../../assets/images/setup-page-hero-large@3x.png';
import setupPageHeroSmallPng from '../../assets/images/setup-page-hero-small.png';
import setupPageHeroSmallWebp from '../../assets/images/setup-page-hero-small.webp';
import setupPageHeroSmall2xPng from '../../assets/images/setup-page-hero-small@2x.png';
import setupPageHeroSmall3xPng from '../../assets/images/setup-page-hero-small@3x.png';
import useResponsiveBreakpoint from '../../hooks/useResponsiveBreakpoint.js';
import styles from './SetupPage.module.css';

interface SetupPageProps {
  availableIntegrations?: IntegrationToOptInStatus[]; // information about the integrated platforms that may or may not have opted in for instant payouts
  isLoading: boolean;
  locationName: string;
  onSetupClick?: (selectedIntegration?: IntegrationToOptInStatus) => void;
}

const PLATFORM_I18N_KEYS = {
  [InstantAccessProgram_Platform.UNKNOWN_PLATFORM_DO_NOT_USE]: null,
  [InstantAccessProgram_Platform.DOORDASH]: 'bankingInstantPayouts.platforms.doordash',
  [InstantAccessProgram_Platform.UBER_EATS]: 'bankingInstantPayouts.platforms.uberEats',
  [InstantAccessProgram_Platform.GRUBHUB]: 'bankingInstantPayouts.platforms.grubhub',
} as const satisfies Record<InstantAccessProgram_Platform, string | null>;

export const SetupPage = ({
  availableIntegrations = [], onSetupClick, isLoading, locationName,
}: SetupPageProps) => {
  const { t, i18n } = useTranslation();
  const isMobile = useResponsiveBreakpoint();
  const TOS_LINK = 'https://squareup.com/campaign/instant-payouts-terms';
  const LEARN_MORE_LINK = 'https://squareup.com/help/us/en/article/8319-instant-payouts-with-delivery-partners';

  const hasIntegrations = availableIntegrations.length > 0;

  const platformNames = availableIntegrations.flatMap(({ platform }) => {
    const i18nKey = platform && PLATFORM_I18N_KEYS[platform];
    return i18nKey ? [t(i18nKey) as string] : [];
  });

  const platformList = platformNames.length > 0
    ? new Intl.ListFormat(i18n.language, { style: 'long', type: 'conjunction' }).format(platformNames)
    : '';

  const renderDescription = () => (
    hasIntegrations
      ? (
        <Trans
          i18nKey="bankingInstantPayouts.setupPage.descriptionWithPlatforms"
          values={{ platformList }}
        />
      )
      : (
        <Trans i18nKey="bankingInstantPayouts.setupPage.description" />
      )
  );

  const renderSetupButtons = () => {
    if (hasIntegrations) {
      const buttonLabel = isMobile
        ? t('bankingInstantPayouts.setupPage.switchPayoutButtonMobile')
        : t('bankingInstantPayouts.setupPage.switchPayoutButton');
      return (
        <MarketButton
          rank="primary"
          isLoading={isLoading}
          onClick={() => onSetupClick?.()}
        >
          {buttonLabel}
        </MarketButton>
      );
    }
    return (
      <MarketButton
        rank="primary"
        isLoading={isLoading}
        onClick={() => onSetupClick?.()}
      >
        {t('bankingInstantPayouts.setupPage.setupButton')}
      </MarketButton>
    );
  };

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <h1 data-testid="setup-page-title" className={`heading-30 ${styles.title}`}>
          {t('bankingInstantPayouts.setupPage.title', { locationName })}
        </h1>

        <p data-testid="setup-page-description" className={`${styles.description} paragraph-30`}>
          {renderDescription()}
        </p>

        <div className={styles.requirements}>
          <p className={`paragraph-10 ${styles.requirements}`}>
            <Trans
              i18nKey="bankingInstantPayouts.setupPage.requirementsAndTerms"
              components={{
                termsLink: (
                  <MarketLink
                    className={styles.link}
                    href={TOS_LINK}
                    target="_blank"
                    rel="noopener noreferrer"
                  />
                ),
              }}
            />
          </p>
        </div>
      </div>

      <div className={styles.actions}>
        {renderSetupButtons()}
        <MarketButton
          rank="secondary"
          href={LEARN_MORE_LINK}
          target="_blank"
          rel="noopener noreferrer"
        >
          {t('bankingInstantPayouts.setupPage.learnMore')}
        </MarketButton>
      </div>

      {/* WebP with mobile/desktop variants, falling back to PNG with 1x/2x/3x for retina displays */}
      <picture>
        <source
          media="(max-width: 800px)"
          srcSet={setupPageHeroSmallWebp}
          type="image/webp"
        />
        <source
          srcSet={setupPageHeroLargeWebp}
          type="image/webp"
        />
        <source
          media="(max-width: 800px)"
          srcSet={`${setupPageHeroSmallPng} 1x, ${setupPageHeroSmall2xPng} 2x, ${setupPageHeroSmall3xPng} 3x`}
          type="image/png"
        />
        <source
          srcSet={`${setupPageHeroLargePng} 1x, ${setupPageHeroLarge2xPng} 2x, ${setupPageHeroLarge3xPng} 3x`}
          type="image/png"
        />
        <img
          src={setupPageHeroLargePng}
          alt=""
          className={styles.hero}
        />
      </picture>
    </div>
  );
};

export const SetupPageI18n = withI18n(SetupPage);
